import { useSWService } from 'src/api/sw/useSWService';
import { useAppStore } from 'src/context/AppContext';
import { AppModalData } from 'src/types/ModalData';

const useTriggerReleaseVersionCheck = () => {
    const { setAppModalData } = useAppStore();
    const { clearCache } = useSWService();

    const refreshTitle = 'A new version of OrcaWeb is available!';
    const refreshMessage =
        'Please refresh. Continuing on this version might cause errors. If you have unsaved work, select Cancel, save your work and refresh the website as soon as possible.';
    const refreshActionButtonText = 'Refresh';

    const updateInProgressTitle = 'System Update in Progress';
    const updateInProgressMessage =
        'OrcaWeb is currently being updated. The application may be unresponsive for up to 15 minutes. Please wait for the update to complete before continuing.';
    const updateInProgressActionButtonText = 'OK';

    /**
     * Compare two version strings to determine if version1 is newer than version2
     * Handles semantic versioning (x.y.z) and build numbers
     * @param version1 - First version to compare
     * @param version2 - Second version to compare
     * @returns true if version1 is newer than version2
     */
    const isVersionNewer = (version1: string, version2: string): boolean => {
        if (!version1 || !version2) return false;

        // Handle case where versions are identical
        if (version1 === version2) return false;

        // Simple string comparison for build IDs or timestamps
        // If they contain only numbers, treat as build numbers
        if (/^\d+$/.test(version1) && /^\d+$/.test(version2)) {
            return parseInt(version1, 10) > parseInt(version2, 10);
        }

        // For semantic versioning or other formats, use string comparison as fallback
        // This could be enhanced based on the actual version format used
        return version1 > version2;
    };

    const refresh = async () => {
        // Write grace period entry so the next poll suppresses the modal while AFD propagates
        const apiVersion = sessionStorage.getItem('lastMismatchApiVersion');
        if (apiVersion) {
            sessionStorage.setItem('refreshAttemptVersion', JSON.stringify({ version: apiVersion, timestamp: Date.now() }));
            sessionStorage.removeItem('lastMismatchApiVersion');
        }
        // Clear postpone tracking since user explicitly refreshed
        sessionStorage.removeItem('postponedVersionId');
        localStorage.removeItem('postponedVersion');
        sessionStorage.removeItem('lastUpdateModalTime');
        try {
            await clearCache();
        } finally {
            window.location.reload();
        }
    };

    const cancelRefresh = (versionId?: string) => {
        const APP_REFRESH_POSTPONE_MINUTES = 10;
        const now = new Date();
        const delayToTime = new Date(now.setMinutes(now.getMinutes() + APP_REFRESH_POSTPONE_MINUTES));

        if (versionId) {
            localStorage.setItem('postponedVersion', JSON.stringify({ version: versionId, expiry: delayToTime.getTime() }));
        }
    };

    const triggerModal = (releaseIdFromApi: string) => {
        // Store the API version so refresh() can write the AFD grace period entry
        sessionStorage.setItem('lastMismatchApiVersion', releaseIdFromApi);
        const _appModalData: AppModalData = {
            title: refreshTitle,
            message: refreshMessage,
            actionButtonText: refreshActionButtonText,
            action: refresh,
            cancel: () => cancelRefresh(releaseIdFromApi),
        };
        setAppModalData({ ..._appModalData });
    };

    const triggerUpdateInProgressModal = () => {
        const _appModalData: AppModalData = {
            title: updateInProgressTitle,
            message: updateInProgressMessage,
            actionButtonText: updateInProgressActionButtonText,
            action: () => {}, // Just close the modal, no action needed
            // No cancel option for this modal - user just needs to acknowledge
        };
        setAppModalData({ ..._appModalData });
    };

    const releaseVersionCheck = (releaseIdFromApi, appSettingsReleaseId) => {
        // compares releaseId from API with current displayed website version,
        // if mismatch, setAppRequiresRefresh which should trigger a popup in RouterContent.tsx
        // User can postpone this popup by clicking cancel
        if (!releaseIdFromApi || releaseIdFromApi?.length < 1) return;
        if (releaseIdFromApi === appSettingsReleaseId) {
            // Versions match — clear all tracking since update is complete
            sessionStorage.removeItem('lastUpdateModalTime');
            sessionStorage.removeItem('refreshAttemptVersion');
            sessionStorage.removeItem('lastMismatchApiVersion');
            localStorage.removeItem('postponedVersion');
            return;
        }
        if (appSettingsReleaseId === 'localhost') return;

        // Handle case where frontend version is newer than API version (deployment in progress)
        if (isVersionNewer(appSettingsReleaseId, releaseIdFromApi)) {
            // Frontend is newer - likely a deployment in progress
            console.log('Frontend version is newer than API version - deployment in progress');

            // Check if we already showed the update-in-progress modal recently to avoid spam
            const lastUpdateModalTime = sessionStorage.getItem('lastUpdateModalTime');
            const UPDATE_MODAL_COOLDOWN_MINUTES = 5;
            const now = new Date().getTime();

            if (!lastUpdateModalTime || now - parseInt(lastUpdateModalTime, 10) > UPDATE_MODAL_COOLDOWN_MINUTES * 60 * 1000) {
                // Show the update-in-progress modal and record the time
                sessionStorage.setItem('lastUpdateModalTime', now.toString());
                triggerUpdateInProgressModal();
            }

            return;
        }

        // Check localStorage postpone (shared across tabs, survives reload)
        try {
            const postponed = localStorage.getItem('postponedVersion');
            if (postponed) {
                const { version, expiry } = JSON.parse(postponed);
                if (version === releaseIdFromApi && expiry > Date.now()) {
                    return; // Postponed — don't show modal
                }
                localStorage.removeItem('postponedVersion');
            }
        } catch {
            localStorage.removeItem('postponedVersion');
        }

        // Check sessionStorage grace period (AFD cache propagation delay, up to 15 min)
        try {
            const refreshAttempt = sessionStorage.getItem('refreshAttemptVersion');
            if (refreshAttempt) {
                const { version, timestamp } = JSON.parse(refreshAttempt);
                const GRACE_PERIOD_MINUTES = 15;
                if (version === releaseIdFromApi && Date.now() - timestamp < GRACE_PERIOD_MINUTES * 60 * 1000) {
                    return; // Suppress — user already refreshed, AFD still propagating
                }
                sessionStorage.removeItem('refreshAttemptVersion');
            }
        } catch {
            sessionStorage.removeItem('refreshAttemptVersion');
        }

        triggerModal(releaseIdFromApi);
    };

    return { releaseVersionCheck };
};

export default useTriggerReleaseVersionCheck;
